import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styles from "./UnusedEntitlement.module.css";
import panel from "../admin/AdminPanel.module.css";
import type { UnusedSeatBaselineSnapshotResponse } from "../../api/organisationReporting";

/** One capture: when it was taken and each tool's row in it. */
interface Capture {
  capturedAt: string;
  rows: Map<string, UnusedSeatBaselineSnapshotResponse>;
  thresholdDays: number;
}

/**
 * The `SC-9` record of unused seats over time: a dated snapshot of each tool's unused seats, taken whenever someone captures one, so the
 * effect of giving seats up shows from one snapshot to the next (`OQ-19`). No capture is ever replaced. The latest snapshot is shown as a
 * tile per tool with its change since the one before, and every snapshot as a row of a table, newest first, a column per tool.
 *
 * @param props.snapshots - Every captured row, across every capture and every tool.
 * @param props.canCapture - Whether the signed-in person may capture a new snapshot.
 * @param props.capturing - Whether a capture request is in flight.
 * @param props.onCapture - Called when the person asks to capture a new snapshot.
 * @param props.exportAction - The CSV export button, for the panel's header.
 */
export function UnusedSeatBaselineSection({
  snapshots,
  canCapture,
  capturing,
  onCapture,
  exportAction,
}: {
  snapshots: UnusedSeatBaselineSnapshotResponse[];
  canCapture: boolean;
  capturing: boolean;
  onCapture: () => void;
  exportAction?: React.ReactNode;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();

  const { captures, tools } = useMemo(() => {
    const byCapture = new Map<string, Capture>();
    const names = new Map<string, string>();
    for (const row of snapshots) {
      const capture = byCapture.get(row.capturedAt) ?? { capturedAt: row.capturedAt, rows: new Map(), thresholdDays: row.thresholdDaysUsed };
      capture.rows.set(row.vendorId.toLowerCase(), row);
      byCapture.set(row.capturedAt, capture);
      names.set(row.vendorId.toLowerCase(), row.vendorDisplayName);
    }

    return {
      captures: [...byCapture.values()].sort((a, b) => Date.parse(b.capturedAt) - Date.parse(a.capturedAt)),
      tools: [...names.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [snapshots]);

  const dateTime = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }), [i18n.language]);
  const percent = useMemo(() => new Intl.NumberFormat(i18n.language, { style: "percent", maximumFractionDigits: 0 }), [i18n.language]);
  const count = (value: number): string => value.toLocaleString(i18n.language);

  const latest = captures[0];
  const previous = captures[1];

  return (
    <section className={panel.panel} aria-labelledby="baseline-heading">
      <div className={panel.header}>
        <h2 id="baseline-heading" className={`panel-title ${panel.title}`}>
          {t("unusedEntitlement.baseline.title")}
          <span className={styles.count}>{count(captures.length)}</span>
        </h2>
        <div className={styles.headerActions}>
          {canCapture && (
            <button type="button" className="button button-primary" disabled={capturing} onClick={onCapture}>
              {capturing ? t("unusedEntitlement.baseline.capturing") : t("unusedEntitlement.baseline.captureAction")}
            </button>
          )}
          {exportAction}
        </div>
      </div>
      <p className={panel.intro}>{t("unusedEntitlement.baseline.explanation")}</p>

      {latest === undefined ? (
        <p className={styles.note}>{t("unusedEntitlement.baseline.none")}</p>
      ) : (
        <>
          <div>
            <h3 className={panel.subheading}>{t("unusedEntitlement.baseline.latest", { date: dateTime.format(new Date(latest.capturedAt)) })}</h3>
            <dl className={panel.tiles}>
              {tools
                .filter((tool) => latest.rows.has(tool.id))
                .map((tool) => {
                  const row = latest.rows.get(tool.id)!;
                  const before = previous?.rows.get(tool.id);
                  const change = before === undefined ? null : row.unusedCount - before.unusedCount;
                  return (
                    <div key={tool.id} className={panel.tile}>
                      <dt>{tool.name}</dt>
                      <dd>
                        {t("unusedEntitlement.baseline.unusedOf", { unused: count(row.unusedCount), total: count(row.totalEntitledCount) })}
                        <span className={panel.tileNote}>
                          {row.totalEntitledCount > 0 && `${percent.format(row.unusedCount / row.totalEntitledCount)} · `}
                          {change === null
                            ? t("unusedEntitlement.baseline.noEarlier")
                            : change === 0
                              ? t("unusedEntitlement.baseline.unchanged")
                              : t(change > 0 ? "unusedEntitlement.baseline.up" : "unusedEntitlement.baseline.down", { count: Math.abs(change) })}
                        </span>
                        {row.unknownCount > 0 && <span className={panel.tileNote}>{t("unusedEntitlement.baseline.unknownNote", { count: row.unknownCount })}</span>}
                      </dd>
                    </div>
                  );
                })}
            </dl>
          </div>

          <div>
            <h3 className={panel.subheading}>{t("unusedEntitlement.baseline.history")}</h3>
            <div className={styles.tableWrapper} tabIndex={0} role="group" aria-label={t("unusedEntitlement.baseline.history")}>
              <table className={`${styles.table} ${styles.historyTable}`}>
                <caption className="visually-hidden">{t("unusedEntitlement.baseline.historyCaption")}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t("unusedEntitlement.baseline.table.capturedAt")}</th>
                    {tools.map((tool) => (
                      <th key={tool.id} scope="col" className={styles.number}>
                        {tool.name}
                      </th>
                    ))}
                    <th scope="col" className={styles.number}>
                      {t("unusedEntitlement.baseline.table.threshold")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {captures.map((capture) => (
                    <tr key={capture.capturedAt}>
                      <th scope="row">{dateTime.format(new Date(capture.capturedAt))}</th>
                      {tools.map((tool) => {
                        const row = capture.rows.get(tool.id);
                        return (
                          <td key={tool.id} className={styles.number}>
                            {row === undefined ? (
                              <span className={styles.muted}>{t("unusedEntitlement.baseline.notRecorded")}</span>
                            ) : (
                              <>
                                {t("unusedEntitlement.baseline.unusedOf", { unused: count(row.unusedCount), total: count(row.totalEntitledCount) })}
                                {row.unknownCount > 0 && (
                                  <span className={styles.caveat}>{t("unusedEntitlement.baseline.unknownNote", { count: row.unknownCount })}</span>
                                )}
                              </>
                            )}
                          </td>
                        );
                      })}
                      <td className={styles.number}>{t("unusedEntitlement.finding.days", { count: capture.thresholdDays })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
